<div class="aic-mobile-page aic-audit-page">
  <Navbar />
  <Sidebar />

  <div v-if="is_loading" class="loading-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
    <div class="text-center">
      <div class="custom-spinner"></div>
      <p class="mt-2 small text-muted">Loading audit logs...</p>
    </div>
  </div>

  <div class="page-wrapper aic-mobile-content">
    <div class="hero-section w-100 aic-safe-bottom">
      <div class="border border-secondary rounded-4 p-3 audit-mobile-shell">

        <!-- Header (centered as in original) -->
        <div class="text-center mb-4">
          <h2 class="gradient-text fw-bold">Activity Logs</h2>
          <p class="text-muted mb-0">View and monitor user activities across the system.</p>
        </div>

        <!-- Stat cards -->
        <div class="row text-center mb-3">
          <div class="col-md-4 mb-3" v-for="(s, key) in stat_cards" :key="key">
            <div class="card rounded-4 shadow-sm users-card">
              <div class="card-body">
                <div class="card-text display-5" :class="s.val_class">{{ s.value }}</div>
                <div class="card-title">{{ s.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter bar -->
        <div class="d-flex flex-wrap gap-2 align-items-center mb-4">
          <div class="position-relative flex-grow-1">
            <i class="fas fa-search position-absolute top-50 translate-middle-y text-muted search-icon"></i>
            <input
              v-model="search_query"
              type="text"
              class="form-control audit-input ps-5"
              placeholder="Search by name, email or activity…"
              @input="on_search_input"
            />
          </div>

          <div class="position-relative audit-select-wrap">
            <select v-model="active_platform" class="form-select audit-select w-auto pe-4" @change="switch_platform(active_platform)">
              <option v-for="tab in platform_tabs" :key="tab.key" :value="tab.key">{{ tab.label }}</option>
            </select>
            <i class="fas fa-chevron-down audit-select-chevron"></i>
          </div>

          <div class="position-relative audit-select-wrap">
            <select v-model="filter_risk" class="form-select audit-select w-auto pe-4" @change="refetch">
              <option value="">All risks</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
            <i class="fas fa-chevron-down audit-select-chevron"></i>
          </div>

          <div class="position-relative audit-select-wrap">
            <select v-model="filter_date_range" class="form-select audit-select w-auto pe-4" @change="refetch">
              <option value="">All time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
            </select>
            <i class="fas fa-chevron-down audit-select-chevron"></i>
          </div>

          <button v-if="has_active_filters" class="btn audit-clear-btn d-flex align-items-center gap-1" @click="clear_filters">
            <i class="fas fa-times"></i> Clear
          </button>
        </div>

        <!-- All-platforms notice -->
        <div v-if="active_platform === 'all'" class="audit-notice d-flex align-items-center gap-2 mb-3">
          <i class="fas fa-clock-rotate-left fa-xs"></i>
          <span>Fetching from all platforms may take a moment.</span>
        </div>

        <!-- User list -->
        <div class="d-flex flex-column gap-2">

          <!-- Empty state -->
          <div v-if="display_groups.length === 0 && !is_loading" class="text-center py-5">
            <div class="fw-medium">No records found</div>
            <div class="text-muted small mt-1">Adjust your filters or select a different platform.</div>
          </div>

          <!-- User rows -->
          <div
            v-for="group in display_groups"
            :key="group.user_email"
            class="audit-row rounded-3 overflow-hidden"
            :class="{ 'is-open': expanded_users.has(group.user_email) }"
            title="Click to expand user events"
          >
            <!-- Row summary -->
            <div class="audit-row-head d-flex align-items-center gap-3 px-3 py-3" @click="toggle_user(group.user_email)">
              <div class="user-avatar flex-shrink-0 d-none d-md-flex align-items-center justify-content-center rounded-3 fw-bold">
                {{ group.initials }}
              </div>

              <!-- Name & email centered in the remaining space (matches screenshot) -->
              <div class="flex-grow-1 overflow-hidden">
                <div class="user-name text-truncate">{{ group.user_full_names || 'Unknown user' }}</div>
                <div class="user-email text-truncate">{{ group.user_email }}</div>
              </div>

              <div class="d-flex align-items-center gap-2 flex-shrink-0">
                <span class="event-count">{{ group.total_events }} event{{ group.total_events !== 1 ? 's' : '' }}</span>
                <span v-if="group.dominant_risk" :class="['risk-level', group.dominant_risk.toLowerCase()]">{{ group.dominant_risk }}</span>
              </div>

              <i class="fas fa-chevron-right chevron flex-shrink-0 text-muted" title="dropdown"
              :class="{ 'chevron-open': expanded_users.has(group.user_email) }"></i>

            </div>

            <!-- Expanded events panel -->
            <transition name="slide">
              <div v-if="expanded_users.has(group.user_email)" class="audit-events-wrap border-top">
                <div class="table-responsive">
                  <table class="audit-table w-100">
                    <thead>
                      <tr>
                        <th>No.</th>
                        <th>Platform</th>
                        <th>Date &amp; Time</th>
                        <th>Activity</th>
                        <th>Risk</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in paginated_events(group.user_email, group.events)" :key="item.id">
                        <td class="row-num">{{ (get_user_event_page(group.user_email) - 1) * 15 + index + 1 }}</td>
                        <td><span class="platform-tag">{{ item.source }}</span></td>
                        <td>
                          <span class="dt-date d-block">{{ format_datetime(item.date_created).date }}</span>
                          <span class="dt-time">{{ format_datetime(item.date_created).time }}</span>
                        </td>
                        <td class="activity-desc">{{ item.description }}</td>
                        <td>
                          <span v-if="item.risk_level" :class="['risk-level small', item.risk_level.toLowerCase()]">{{ item.risk_level }}</span>
                        </td>
                        <td>
                          <button class="btn btn-sm btn-success text-nowrap" @click.stop="go_to_page(item.page_url)">View</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Per-user pagination -->
                <div v-if="user_total_pages(group.events) > 1" class="events-pagination d-flex justify-content-between align-items-center px-3 py-2">
                  <small class="text-muted">
                    {{ user_event_start(group.user_email) }}–{{ user_event_end(group.user_email, group.events) }}
                    of {{ group.events.length }}
                  </small>
                  <div class="d-flex gap-1 align-items-center">
                    <button class="btn page-btn" :disabled="get_user_event_page(group.user_email) === 1" @click.stop="user_prev_page(group.user_email)">
                      <i class="fas fa-chevron-left fa-xs"></i>
                    </button>
                    <template v-for="p in user_page_range(group.user_email, group.events)" :key="p">
                      <span v-if="p === '...'" class="px-1 text-muted small">…</span>
                      <button v-else class="btn page-btn" :class="{ active: p === get_user_event_page(group.user_email) }" @click.stop="user_go_to_page(group.user_email, p)">{{ p }}</button>
                    </template>
                    <button class="btn page-btn" :disabled="get_user_event_page(group.user_email) === user_total_pages(group.events)" @click.stop="user_next_page(group.user_email, group.events)">
                      <i class="fas fa-chevron-right fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Outer pagination -->
        <div v-if="pagination.num_pages > 1" class="outer-pagination d-flex justify-content-between align-items-center mt-4 pt-3">
          <small class="text-muted">
            Page {{ pagination.current_page }} of {{ pagination.num_pages }}
            <span v-if="pagination.total">&nbsp;· {{ pagination.total }} users</span>
          </small>
          <div class="d-flex gap-1 align-items-center">
            <button class="btn page-btn px-3" :disabled="!pagination.has_previous" @click="previous_page">
              <i class="fas fa-chevron-left fa-xs me-1"></i>Prev
            </button>
            <template v-for="p in page_range" :key="p">
              <span v-if="p === '...'" class="px-1 text-muted small">…</span>
              <button v-else class="btn page-btn" :class="{ active: p === pagination.current_page }" @click="fetch_for_platform(active_platform, p)">{{ p }}</button>
            </template>
            <button class="btn page-btn px-3" :disabled="!pagination.has_next" @click="next_page">
              Next<i class="fas fa-chevron-right fa-xs ms-1"></i>
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>
